import Pagination from 'components/Pagination'
import React from 'react'
import { useQuery } from '@apollo/client'
import {
  lotsListQuery,
  lotsListQueryVariables
} from 'components/LotsList/types/lotsListQuery'
import Loading from 'components/Loading'
import PageError from 'components/pages/PageError'
import LotCard from 'components/LotCard'
import pathFromUrl from 'utils/pathFromUrl'
import { LocationMarkerIcon } from '@heroicons/react/outline'
import { usePagination } from 'url-pagination'
import cn from 'classnames'

import QUERY from './query'

type Props = {
  marketId: number
  marketName: string
  whiteBg?: boolean
}

const LotsList = ({ marketId, marketName, whiteBg = false }: Props) => {
  const pagination = usePagination({ perPage: 6 })

  const { data, error, previousData } = useQuery<
    lotsListQuery,
    lotsListQueryVariables
  >(QUERY, {
    fetchPolicy: 'cache-and-network',
    variables: {
      marketId,
      lotsOffset: pagination.offset,
      lotsLimit: pagination.perPage
    },
    skip: !marketId
  })

  const loading = (!data || !data.lots) && !previousData && !error
  if (loading)
    return (
      <div className="w-full h-72 flex justify-center items-center">
        <Loading />
      </div>
    )
  if (error) return <PageError>{error.message}</PageError>

  const {
    lots: { records: lots, total }
  } = data || previousData!

  if (lots.length === 0) return null

  return (
    <div
      className={cn('w-full py-10', {
        'bg-gradient-to-b from-white to-gray-50': !whiteBg,
        'bg-white': whiteBg
      })}
    >
      <div className="max-w-screen-lg mx-auto md:px-4">
        <div className="flex flex-col items-center md:mb-10 mb-6">
          <div className="p-3 rounded-full bg-gray-700">
            <LocationMarkerIcon className="md:w-7 md:h-7 h-6 w-6 text-gray-50" />
          </div>
          <h2 className="text-2xl md:text-3xl font-bold leading-5 text-gray-700 mt-5 text-center px-4">
            Hot locations in {marketName}
          </h2>
        </div>

        <div className="md:-mx-4 flex flex-wrap box-border">
          {lots.map((lot) => (
            <div key={lot.id} className="md:w-1/2 w-full">
              <LotCard
                address={lot.address}
                image={lot.foodImage!.medium}
                locationsThisDayCount={lot.locationsThisDayCount}
                locationsThisWeekCount={lot.locationsThisWeekCount}
                lotPath={lot.lotUrl ? pathFromUrl(lot.lotUrl) : null}
                name={lot.name}
              />
            </div>
          ))}
        </div>

        <div className="flex justify-center mt-2">
          <Pagination total={total} {...pagination} showEmpty={false} />
        </div>
      </div>
    </div>
  )
}

export default LotsList
